/* eslint-disable camelcase */
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm, FieldErrorsImpl,FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Button from '../../components/buttons/Button'
import InputField from '../../components/input-field/InputField'
import InputPasswordField from '../../components/input-field/InputPasswordField'
import Logo from '../../components/logo/Logo'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import { registerUser, setAuthForm } from '../../store/features/auth/authSlice'
import {
  EnglishCharecterRegex,
  IntegerNumberRegex,
  PersianCharecterRegex,
  PersianMobileNumberRegex,
} from '../../utils/regex'
import { AuthForm } from '../../store/features/auth/type'
import { verifyIranianLegalId, verifyIranianNationalId } from '@persian-tools/persian-tools'

interface IForms {
  name: string
  family: string
  nationalCode: string
  mobile: string
  email?: string
  password: string
  passwordRepeat?: string
}

type IProps = {
  handleLoginClick: () => void
}

const RegisterForm = () => {
  const { t } = useTranslation()
  const { loading } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()

  const schema: yup.SchemaOf<IForms> = yup.object({
    name: yup
      .string()
      .matches(PersianCharecterRegex, {
        message: 'persianCharacter',
        excludeEmptyString: true,
      })
      .required('requiedFeild'),
    family: yup
      .string()
      .matches(PersianCharecterRegex, {
        message: 'persianCharacter',
        excludeEmptyString: true,
      })
      .required('requiedFeild'),
    nationalCode: yup
      .string()
      .matches(IntegerNumberRegex, {
        message: 'integerNumber',
        excludeEmptyString: true,
      })
      .min(10, 'nationalCodeLength')
      .max(10, 'nationalCodeLength')
      .required('requiedFeild')
      .test('verifyNationaCode', 'notValidNationalCode', (value) => {
        return verifyIranianNationalId(value) as boolean
      }),

    mobile: yup
      .string()
      .matches(IntegerNumberRegex, {
        message: 'integerNumber',
        excludeEmptyString: true,
      })
      .min(11, 'mobileLength')
      .max(11, 'mobileLength')
      .matches(PersianMobileNumberRegex, {
        message: 'mobilePattern',
        excludeEmptyString: true,
      })
      .required('requiedFeild'),
    email: yup
      .string()
      .matches(EnglishCharecterRegex, {
        message: 'englishCharacter',
        excludeEmptyString: true,
      })
      .email('emailPattern'),
    password: yup.string().min(8, 'passwordMinLength').required('requiedFeild'),
    passwordRepeat: yup
      .string()
      .min(8, 'passwordMinLength')
      .oneOf([yup.ref('password'), null], 'matchPassword'),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IForms>({
    resolver: yupResolver(schema),
  })

  const handleRegisterClick: SubmitHandler<IForms> = (data, event) => {
    event?.preventDefault()
    dispatch(registerUser(data))
  }

  const handleLoginKeyPress = (e: any) => {
    if (e.keyCode === 13 || e.key === 'Enter') {
      handleSubmit(handleRegisterClick)()
    }
  }

  const handleSetAuthFormClick = () => {
    dispatch(setAuthForm(AuthForm.LOGIN))
  }

  return (
    <div className='flex items-center justify-center w-full h-full bg-neutral-50'>
      <div className='bg-white my-10 lg:w-[30%] md:w-[40%] w-4/5 shadow-lg rounded-xl'>
        {/* Login form section */}
        <div className='h-full w-full flex flex-col items-center justify-center p-8 lg:py-8 lg:px-4 '>
          <div className='flex lg:flex-row flex-col  items-center justify-center lg:justify-between md:-mr-3'>
            <div className='w-full lg:w-14 flex items-center justify-center mb-2'>
              <Logo classes='w-14 h-14 md:w-10 md:h-10' />
            </div>
            <p className='text-sm mr-2 mt-2 text-gray-500 text-center leading-7'>
              {t('loginWelcome')}
            </p>
          </div>
          <p className='text-sm mt-4 text-blue-500'>ثبت نام حساب کاربری</p>
          <div className='w-full lg:w-4/5 mt-6'>
            <form className='w-full h-full mb-8 grid grid-cols-1 gap-4 items-center justify-between'>
              <InputField
                id={'firstName'}
                lableTitle={t('firstName')}
                errors={errors as FieldErrorsImpl<FieldValues>}
                {...register('name')}
              />
              <InputField
                id={'lastName'}
                lableTitle={t('lastName')}
                errors={errors as FieldErrorsImpl<FieldValues>}
                {...register('family')}
              />
              <InputField
                id={'nationalCode'}
                lableTitle={t('nationalCode')}
                maxLength={10}
                errors={errors as FieldErrorsImpl<FieldValues>}
                {...register('nationalCode')}
              />
              <InputField
                id={'mobile'}
                lableTitle={t('mobile')}
                maxLength={11}
                errors={errors as FieldErrorsImpl<FieldValues>}
                {...register('mobile')}
              />
              <InputField
                id={'email'}
                lableTitle={t('email')}
                errors={errors as FieldErrorsImpl<FieldValues>}
                {...register('email')}
              />
              <InputPasswordField
                id={'password'}
                lableTitle={t('password')}
                errors={errors as FieldErrorsImpl<FieldValues>}
                {...register('password')}
              />
              <InputPasswordField
                onKeyDown={handleLoginKeyPress}
                id={'passwordRepeat'}
                lableTitle={t('passwordRepeat')}
                errors={errors as FieldErrorsImpl<FieldValues>}
                {...register('passwordRepeat')}
              />
            </form>
            <div className='mt-6 mb-4 w-full'>
              <Button
                type='submit'
                disabled={loading}
                loading={loading}
                types='success'
                classes='w-full'
                onClick={handleSubmit(handleRegisterClick)}
              >
                {t('register')}
              </Button>
              <Button
                className='w-full mt-4 px-4 py-2 rounded-lg text-sm duration-300 ease-in-out border border-transparent hover:border hover:text-blue-400 hover:border-blue-400 text-gray-400'
                onClick={handleSetAuthFormClick}
              >
                {t('loginPage')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterForm
